import type { AtlasData } from './WasmBridge';

export interface SectionMeshResult {
  cx: number;
  sy: number;
  cz: number;
  vertices: Float32Array;
  indices: Uint32Array;
}

export interface BlockUpdate {
  wx: number;
  wy: number;
  wz: number;
  id: number;
  meta: number;
}

// Main-thread handle to the off-thread WASM mesher. The worker hosts the WASM
// runtime, the block world and all meshing; this side only forwards SignalR
// payloads in and hands finished section meshes to the renderer.
export class MeshWorkerClient {
  private readonly worker: Worker;
  private readonly ready: Promise<AtlasData>;
  private meshCb: ((m: SectionMeshResult) => void) | null = null;
  private progressCb: ((message: string) => void) | null = null;
  private settled = false;

  constructor() {
    this.worker = new Worker(new URL('./meshWorker.ts', import.meta.url), { type: 'module' });

    let resolveReady: (a: AtlasData) => void;
    let rejectReady: (e: Error) => void;
    this.ready = new Promise<AtlasData>((res, rej) => { resolveReady = res; rejectReady = rej; });

    const settle = (fn: () => void) => {
      if (this.settled) return;
      this.settled = true;
      fn();
    };

    this.worker.onmessage = (ev: MessageEvent) => {
      const msg = ev.data;
      switch (msg.type) {
        case 'progress':
          this.progressCb?.(msg.message as string);
          break;
        case 'ready':
          settle(() => resolveReady(msg.atlas as AtlasData));
          break;
        case 'mesh':
          this.meshCb?.({
            cx: msg.cx,
            sy: msg.sy,
            cz: msg.cz,
            vertices: msg.empty ? new Float32Array(0) : msg.vertices,
            indices: msg.empty ? new Uint32Array(0) : msg.indices,
          });
          break;
        case 'error':
          console.error('[meshWorker]', msg.message);
          settle(() => rejectReady(new Error(msg.message)));
          break;
      }
    };
    // A worker module that fails to load or a hard crash never sends a message —
    // surface it as a rejection so init() can't hang forever.
    this.worker.onerror = (e) => {
      const message = e.message || 'mesh worker failed to start';
      console.error('[meshWorker]', message);
      settle(() => rejectReady(new Error(message)));
    };
    this.worker.onmessageerror = () => {
      settle(() => rejectReady(new Error('mesh worker message deserialization failed')));
    };
    // The worker boots the WASM runtime on its own at module load — no init
    // message. Commands below are only sent after init() resolves.
  }

  init(): Promise<AtlasData> {
    return this.ready;
  }

  onProgress(cb: (message: string) => void): void {
    this.progressCb = cb;
  }

  onMesh(cb: (m: SectionMeshResult) => void): void {
    this.meshCb = cb;
  }

  applyChunk(cx: number, cz: number, compressed: Uint8Array, biomes: Uint8Array): void {
    this.worker.postMessage({ type: 'chunk', cx, cz, data: compressed, biomes });
  }

  setBlocks(updates: BlockUpdate[]): void {
    if (updates.length === 0) return;
    this.worker.postMessage({ type: 'blocks', updates });
  }

  unloadChunk(cx: number, cz: number): void {
    this.worker.postMessage({ type: 'unload', cx, cz });
  }

  setPlayerPosition(x: number, y: number, z: number): void {
    this.worker.postMessage({ type: 'pos', x, y, z });
  }

  clearWorld(): void {
    this.worker.postMessage({ type: 'clear' });
  }

  terminate(): void {
    this.worker.terminate();
  }
}

let shared: MeshWorkerClient | null = null;

// One worker per document — the .NET WASM runtime can only be created once.
// A boot that fails (e.g. a transient asset fetch error) leaves the worker
// permanently rejected; evict it so the next getMeshWorker() boots fresh
// instead of handing back the dead singleton.
export function getMeshWorker(): MeshWorkerClient {
  if (shared) return shared;
  const client = new MeshWorkerClient();
  shared = client;
  client.init().catch(() => {
    if (shared === client) shared = null;
    client.terminate();
  });
  return client;
}
